import React, { useState } from 'react';
import {
  defaultResponseHandler,
  Divider,
  openNotificationWithIcon,
  PageContent,
  RemoteAccessProfile,
  useAssetParams,
  useAssetQuery,
  useUserinfo,
} from '@mss/mss-component-library';
import { Dropdown, Menu, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import Button from 'jsx/common/Button';
import { ChevronDown } from 'react-feather';
import RDPCollapse from './RDPCollapse';
import { TFunction } from 'i18next';
import Api from 'jsx/sessions/services/Api';
import VNCCollapse from './VNCCollapse';
import SSHCollapse from './SSHCollapse';
import WEBCollapse from './WEBCollapse';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Fetch from '../../../common/util/_fetch';

const profileTypes = (t: TFunction) => [
  {
    key: t('RDP'),
    value: 'rdp',
    Component: RDPCollapse,
  },
  {
    key: t('VNC'),
    value: 'vnc',
    Component: VNCCollapse,
  },
  {
    key: t('SSH'),
    value: 'ssh',
    Component: SSHCollapse,
  },
  {
    key: t('WEB'),
    value: 'web',
    Component: WEBCollapse,
  },
];
type RemoteProfilesProps = {
  assetId?: string;
  fetchAsset?: () => void;
};

function RemoteProfiles({
  assetId: assetIdProps,
  fetchAsset: fetchAssetProps,
}: RemoteProfilesProps) {
  const { assetId: assetIdParams } = useAssetParams();
  const { refetch: queryFetchAsset } = useAssetQuery();
  const fetchAsset = fetchAssetProps ?? queryFetchAsset ?? (() => {});
  const assetId = assetIdProps ?? assetIdParams;
  const [t] = useTranslation(['remoteAccess', 'common']);
  const [hasPermission] = useUserinfo(assetId);
  const [mutatingRows, setMutatingRows] = useState<string[]>([]);
  const [unsavedProfiles, setUnsavedProfiles] = useState<
    { protocol: string; key: string }[]
  >([]);

  const queryClient = useQueryClient();
  const queryKey = ['remote-profiles', assetId!];
  const { data = [], refetch } = useQuery({
    queryKey,
    queryFn: async () =>
      (
        await defaultResponseHandler<
          (RemoteAccessProfile & { id: string; settings: object })[]
        >({
          t,
          request: Fetch.get(
            `/api/remote-profile/?asset=${assetId}&ordering=-created_at`
          ),
        })
      ).data ?? [],
  });

  const submitMutation = useMutation({
    mutationFn: async ({
      isNew,
      values,
    }: {
      isNew: boolean;
      values: any;
      key: string;
    }) => {
      const sanitizedValues = {
        ...values,
        settings: Object.fromEntries(
          Object.entries(values.settings).filter(([key, value]) =>
            key === 'color-depth' ? value !== '' : value !== undefined
          )
        ),
      };
      const fetcher = isNew ? Api.profile.create : Api.profile.update;
      return await (await fetcher(sanitizedValues)).json();
    },
    onMutate: async ({ values }) => {
      setMutatingRows(prev => [...prev, values.key]);
    },
    onError: ({ isNew }) => {
      openNotificationWithIcon(
        notification.error,
        t(`Profile not ${isNew ? 'created' : 'updated'}`),
        t(
          `Due to technical issues the profile could not be ${
            isNew ? 'created' : 'updated'
          }. Please try again later.`
        )
      );
    },
    onSuccess: (data, { key, isNew }) => {
      openNotificationWithIcon(
        notification.success,
        t(`Profile successfully ${isNew ? 'created' : 'updated'}`),
        ''
      );
      if (isNew) {
        setUnsavedProfiles(prev => prev.filter(x => x.key !== key));
        queryClient.setQueryData(queryKey, (old: any) => [data, ...old]);
      } else {
        queryClient.setQueryData(queryKey, (old: any) =>
          old.map((x: any) => (x.id === data.id ? data : x))
        );
      }
    },
    onSettled: (_, __, { values }) => {
      refetch();
      fetchAsset();
      setMutatingRows(prev => prev.filter(x => x !== values.key));
      queryClient.invalidateQueries(['profiles', assetId]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await Api.profile.remove(id);
    },
    onMutate: async id => {
      setMutatingRows(prev => [...prev, id]);
    },
    onError: () => {
      openNotificationWithIcon(
        notification.error,
        t('Profile could not be deleted'),
        t(
          'Due to technical issues the profile could not be deleted. Please try again later'
        )
      );
    },
    onSuccess: (_, id) => {
      queryClient.setQueryData(queryKey, (old: any) =>
        old.filter((x: any) => x.id !== id)
      );
      openNotificationWithIcon(notification.success, t('Profile deleted'), '');
    },
    onSettled: (_, __, id) => {
      refetch();
      setMutatingRows(prev => prev.filter(x => x !== id));
      fetchAsset();
      queryClient.invalidateQueries(['profiles', assetId]);
    },
  });

  const addUnsavedProfile = (protocol: string) => () =>
    setUnsavedProfiles([
      { key: Math.round(Math.random() * 10000000).toFixed(), protocol },
      ...unsavedProfiles,
    ]);

  return (
    <PageContent
      title={t('Remote Access Settings')}
      actions={
        hasPermission('add_remoteprofile') && (
          <Dropdown
            overlay={
              <Menu>
                {profileTypes(t).map(profileType => (
                  <Menu.Item
                    key={profileType.key}
                    onClick={addUnsavedProfile(profileType.value)}
                  >
                    {profileType.key}
                  </Menu.Item>
                ))}
              </Menu>
            }
          >
            <Button variant="primary" ghost data-ta="AddRemoteProfileButton">
              {t('Add remote access profile')}
              <ChevronDown size={14} className="ml-1" />
            </Button>
          </Dropdown>
        )
      }
    >
      <Divider />
      {unsavedProfiles.map(unsavedProfile => {
        const props = {
          asset: assetId!,
          key: unsavedProfile.key,
          readonly: mutatingRows.includes(unsavedProfile.key),
          onSubmit: (values: any) =>
            submitMutation.mutateAsync({
              isNew: true,
              values,
              key: unsavedProfile.key,
            }),
        };
        const Component = profileTypes(t).find(
          ({ value }) => value === unsavedProfile.protocol
        )?.Component;

        return Component ? (
          <Component {...props} />
        ) : (
          <div key={unsavedProfile.key}>{t('Unknown profile')}</div>
        );
      })}
      {data.map(profile => {
        const props: any = {
          asset: assetId!,
          key: profile.id,
          profile,
          deleteProfile: hasPermission('delete_remoteprofile')
            ? () => deleteMutation.mutateAsync(profile.id)
            : undefined,
          onSubmit: (values: any) =>
            submitMutation.mutateAsync({
              isNew: false,
              values,
              key: profile.id,
            }),
          readonly:
            mutatingRows.includes(profile.id) ||
            !hasPermission(['change_remoteprofile']),
        };

        const Component = profileTypes(t).find(
          ({ value }) => value === profile.protocol
        )?.Component;

        return Component ? (
          <Component {...props} />
        ) : (
          <div key={profile.id}>{t('Unknown profile')}</div>
        );
      })}
      {data.length + unsavedProfiles.length < 1 && (
        <h6 className="mt-6">
          {t('No remote access profiles yet configured')}
        </h6>
      )}
    </PageContent>
  );
}

export default RemoteProfiles;
